import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

export interface TeamMember {
  imgPath?: string;
  name: string;
  initials: string;
}

interface ConstructionTableConfig {
  colgroup: ReactNode;
  headers: ReactNode;
  row: ReactNode;
}

interface OperationTableConfig {
  colgroup: ReactNode;
  headers: ReactNode;
  row: ReactNode;
}

interface SummaryTabBaseProps {
  totalConstructionOverriden: number | null;
  setTotalConstructionOverriden: React.Dispatch<React.SetStateAction<number | null>>;
  totalOperationOverriden: number | null;
  setTotalOperationOverriden: React.Dispatch<React.SetStateAction<number | null>>;
  constructionTableConfig: ConstructionTableConfig;
  operationTableConfig: OperationTableConfig;
  totalSubTotalConstruction: number | null;
  totalSubTotalOperation: number | null;
  anyConstruction: boolean;
  anyOperations: boolean;
  calculateNormalized?: (construction: number | null, operation: number | null) => number;
  showNormalizedColumn?: boolean;
  energyEfficiencyTotal?: number | null;
}

const formatNumber = (num: number) => {
  if (Number.isNaN(num)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(num));
};

const parseFormattedNumber = (formattedNum: string) => parseFloat(formattedNum.replace(/,/g, ''));

const SummaryTabBase: React.FC<SummaryTabBaseProps> = ({
  totalConstructionOverriden,
  setTotalConstructionOverriden,
  totalOperationOverriden,
  setTotalOperationOverriden,
  constructionTableConfig,
  operationTableConfig,
  totalSubTotalConstruction,
  totalSubTotalOperation,
  anyConstruction,
  anyOperations,
  calculateNormalized,
  showNormalizedColumn = false,
  energyEfficiencyTotal,
}) => {
  const { t } = useTranslation();

  const [isInputConstructionDisabled, setIsInputConstructionDisabled] = useState<boolean>(true);
  const [isInputOperationDisabled, setIsInputOperationDisabled] = useState<boolean>(true);

  const inputConstructionRef = useRef<HTMLInputElement | null>(null);
  const inputOperationRef = useRef<HTMLInputElement | null>(null);

  const [customConstructionTotal, setCustomConstructionTotal] = useState<string>(
    totalConstructionOverriden !== null ? formatNumber(totalConstructionOverriden) : ''
  );

  const [customOperationTotal, setCustomOperationTotal] = useState<string>(
    totalOperationOverriden !== null ? formatNumber(totalOperationOverriden) : ''
  );

  const [isCustomConstructionInput, setIsCustomConstructionInput] = useState(
    totalConstructionOverriden !== null
  );

  const [isCustomOperationInput, setIsCustomOperationInput] = useState(
    totalOperationOverriden !== null
  );

  const handleInputConstruction = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setCustomConstructionTotal(value);
    }
    setIsCustomConstructionInput(value !== '');
  };

  const handleInputOperation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setCustomOperationTotal(value);
    }
    setIsCustomOperationInput(value !== '');
  };

  const finalTotal =
    (totalConstructionOverriden !== null
      ? totalConstructionOverriden
      : totalSubTotalConstruction || 0) +
    (totalOperationOverriden !== null ? totalOperationOverriden : totalSubTotalOperation || 0);

  const totalNormalized = calculateNormalized
    ? calculateNormalized(
        totalConstructionOverriden !== null
          ? totalConstructionOverriden
          : totalSubTotalConstruction,
        totalOperationOverriden !== null ? totalOperationOverriden : totalSubTotalOperation
      )
    : 0;

  const setCustomConstructionInput = () => {
    if (
      customConstructionTotal &&
      customConstructionTotal !== '' &&
      customConstructionTotal !== '.'
    ) {
      setCustomConstructionTotal(parseFormattedNumber(customConstructionTotal).toString());
    }
    setIsInputConstructionDisabled(false);
  };

  const setCustomOperationInput = () => {
    if (customOperationTotal && customOperationTotal !== '') {
      setCustomOperationTotal(parseFormattedNumber(customOperationTotal).toString());
    }

    setIsInputOperationDisabled(false);
  };

  const handleBlurConstruction = () => {
    if (customConstructionTotal === '.') {
      setCustomConstructionTotal('');
    }
    if (customConstructionTotal === null || customConstructionTotal === '') {
      setIsCustomConstructionInput(false);
      setTotalConstructionOverriden(null);

      if (totalSubTotalConstruction) {
        setCustomConstructionTotal(formatNumber(Number(totalSubTotalConstruction)));
      }
    } else {
      const numericValue = Number(customConstructionTotal);
      setCustomConstructionTotal(
        customConstructionTotal !== '' && customConstructionTotal !== '.'
          ? formatNumber(numericValue)
          : ''
      );

      setTotalConstructionOverriden(numericValue);
    }

    setIsInputConstructionDisabled(true);
  };

  const handleBlurOperation = () => {
    if (customOperationTotal === '.') {
      setCustomOperationTotal('');
    }
    if (customOperationTotal === null || customOperationTotal === '') {
      setIsCustomOperationInput(false);
      setTotalOperationOverriden(null);

      if (totalSubTotalOperation) {
        setCustomOperationTotal(formatNumber(Number(totalSubTotalOperation)));
      }
    } else {
      const numericValue = Number(customOperationTotal);
      setCustomOperationTotal(
        customOperationTotal !== '' && customOperationTotal !== '.'
          ? formatNumber(numericValue)
          : ''
      );
      setTotalOperationOverriden(numericValue);
    }

    setIsInputOperationDisabled(true);
  };

  useEffect(() => {
    if (!isInputOperationDisabled && inputOperationRef.current) {
      inputOperationRef.current.focus();
    }
  }, [isInputOperationDisabled]);

  useEffect(() => {
    if (!isInputConstructionDisabled && inputConstructionRef.current) {
      inputConstructionRef.current.focus();
    }
  }, [isInputConstructionDisabled]);

  useEffect(() => {
    if (!isCustomConstructionInput && isInputConstructionDisabled) {
      if (totalSubTotalConstruction !== null) {
        setCustomConstructionTotal(formatNumber(totalSubTotalConstruction));
      } else {
        setCustomConstructionTotal('');
      }
    }

    if (!isCustomOperationInput && isInputOperationDisabled) {
      if (totalSubTotalOperation !== null) {
        setCustomOperationTotal(formatNumber(totalSubTotalOperation));
      } else {
        setCustomOperationTotal('');
      }
    }
  }, [totalSubTotalConstruction, totalSubTotalOperation]);

  const renderTotalEmissionsHeaders = () => {
    if (showNormalizedColumn) {
      return (
        <>
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th />
              <th style={{ textAlign: 'center' }}>Subtotal Construction</th>
              <th style={{ textAlign: 'center' }}>Subtotal Operation</th>
              <th style={{ textAlign: 'center' }}>Normalized</th>
              <th>Total</th>
            </tr>
          </thead>
        </>
      );
    }
    return (
      <>
        <colgroup>
          <col style={{ width: '25%' }} />
          <col style={{ width: '28%' }} />
          <col style={{ width: '27%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead>
          <tr>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <th />
            <th style={{ textAlign: 'center' }}>Subtotal Construction</th>
            <th style={{ textAlign: 'center' }}>Subtotal Operation</th>
            <th>Total</th>
          </tr>
        </thead>
      </>
    );
  };

  const renderTotalEmissionsRow = () => (
    <tr>
      <td>Sub Total</td>
      <td className="summary-subtotal">
        <div className="row">
          <div className="col-12 col-sm-5 col-md-4 col-lg-6 input-no-padding">
            <input
              ref={inputConstructionRef}
              type="text"
              id="constructionInput"
              value={customConstructionTotal !== null ? customConstructionTotal : ''}
              onChange={handleInputConstruction}
              onBlur={handleBlurConstruction}
              className="calculator-total-input"
              onKeyDown={handleKeyDown}
              disabled={isInputConstructionDisabled}
              title=""
            />
          </div>
          {customConstructionTotal &&
            customConstructionTotal !== '' &&
            customConstructionTotal !== '.' &&
            customConstructionTotal !== undefined && (
              <span>
                t CO<sub>2</sub>e
              </span>
            )}
          <div>
            {isInputConstructionDisabled && (
              <>
                <button
                  type="button"
                  className="gi-icon"
                  onClick={setCustomConstructionInput}
                  id="editConstructionIcon"
                >
                  <FontAwesomeIcon icon="edit" />
                </button>
                <UncontrolledTooltip
                  className="tooltip-toolkit"
                  placement="bottom"
                  target="editConstructionIcon"
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  {t('Custom Subtotal Construction')}
                </UncontrolledTooltip>
              </>
            )}
            {totalConstructionOverriden !== null &&
              (!totalSubTotalConstruction ||
                (totalSubTotalConstruction !== null &&
                  totalSubTotalConstruction.toFixed(2) !==
                    totalConstructionOverriden.toFixed(2))) && (
                <div className="icon-changed-calculator">
                  <span id="totalConstructionOverriden_icon" className="cacl-info_icon">
                    <FontAwesomeIcon icon="info-circle" />
                  </span>
                  <span className="cacl-info_text">Changed</span>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target="totalConstructionOverriden_icon"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    <p>
                      <b>{t('calculatorChangedTooltip')}</b>
                    </p>
                  </UncontrolledTooltip>
                </div>
              )}
          </div>
        </div>
      </td>
      <td className="summary-subtotal">
        <div className="row">
          <div className="col-12 col-sm-5 col-md-4 col-lg-6 input-no-padding">
            <input
              ref={inputOperationRef}
              type="text"
              id="operationInput"
              value={customOperationTotal !== null ? customOperationTotal : ''}
              min="0"
              onChange={handleInputOperation}
              onBlur={handleBlurOperation}
              className="calculator-total-input"
              onKeyDown={handleKeyDown}
              disabled={isInputOperationDisabled}
              title=""
            />
          </div>
          {customOperationTotal !== '' && customOperationTotal !== undefined && (
            <span>
              t CO<sub>2</sub>e
            </span>
          )}
          <div>
            {isInputOperationDisabled && (
              <>
                <button
                  type="button"
                  className="gi-icon"
                  onClick={setCustomOperationInput}
                  id="editOperationIcon"
                >
                  <FontAwesomeIcon icon="edit" />
                </button>
                <UncontrolledTooltip
                  className="tooltip-toolkit"
                  placement="bottom"
                  target="editOperationIcon"
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  {t('Custom Subtotal Operation')}
                </UncontrolledTooltip>
              </>
            )}
            {totalOperationOverriden !== null &&
              (!totalSubTotalOperation ||
                (totalSubTotalOperation !== null &&
                  totalSubTotalOperation.toFixed(2) !== totalOperationOverriden.toFixed(2))) && (
                <div className="icon-changed-calculator">
                  <span id="totalOperationOverriden_icon" className="cacl-info_icon">
                    <FontAwesomeIcon icon="info-circle" />
                  </span>
                  <span className="cacl-info_text">Changed</span>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target="totalOperationOverriden_icon"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    <p>
                      <b>{t('calculatorChangedTooltip')}</b>
                    </p>
                  </UncontrolledTooltip>
                </div>
              )}
          </div>
        </div>
      </td>
      {showNormalizedColumn && (
        <td className="summary-subtotal" style={{ textAlign: 'center' }}>
          {(totalConstructionOverriden !== null ||
            totalOperationOverriden !== null ||
            anyOperations ||
            anyConstruction) && (
            <>
              <NumberFormatterSinSeparador value={totalNormalized} /> t CO<sub>2</sub>e
            </>
          )}
        </td>
      )}
      <td className="summary-subtotal">
        {(totalConstructionOverriden !== null ||
          totalOperationOverriden !== null ||
          anyOperations ||
          anyConstruction) &&
          !Number.isNaN(finalTotal) && (
            <>
              <NumberFormatterSinSeparador value={finalTotal} /> t CO<sub>2</sub>e
            </>
          )}
      </td>
    </tr>
  );

  return (
    <>
      <h4>{t('construction')}</h4>
      <div className="table-wrapper sticky-section-calculator w-100">
        <Table
          className="table--bordered calculator table-details table calc-subtotal"
          style={{ tableLayout: 'fixed' }}
        >
          {constructionTableConfig.colgroup}
          <thead>{constructionTableConfig.headers}</thead>
          <tbody>{constructionTableConfig.row}</tbody>
          <tfoot>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td colSpan={12} />
            </tr>
          </tfoot>
        </Table>
      </div>

      <h4>{t('operation')}</h4>
      <div className="table-wrapper sticky-section-calculator w-100">
        <Table
          className="table--bordered calculator table-details table calc-subtotal"
          style={{ tableLayout: 'fixed' }}
        >
          {operationTableConfig.colgroup}
          <thead>{operationTableConfig.headers}</thead>
          <tbody>{operationTableConfig.row}</tbody>
          <tfoot>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td colSpan={5} />
            </tr>
          </tfoot>
        </Table>
      </div>

      <h4>{t('Total Emissions')}</h4>
      <div className="table-wrapper sticky-section-calculator w-100">
        <Table
          className="table--bordered calculator table-details table calc-subtotal"
          style={{ tableLayout: 'fixed' }}
        >
          {renderTotalEmissionsHeaders()}
          <tbody>
            {renderTotalEmissionsRow()}
            {energyEfficiencyTotal !== undefined && (
              <tr>
                <td colSpan={2}>
                  <span className="subtitle-avoided">
                    Avoided Emissions from Energy Efficiency Upgrades:
                  </span>
                  {energyEfficiencyTotal && (
                    <span>
                      &nbsp;
                      <NumberFormatterSinSeparador value={energyEfficiencyTotal} /> t CO<sub>2</sub>
                      e
                    </span>
                  )}
                </td>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td colSpan={showNormalizedColumn ? 3 : 2} />
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td colSpan={5} />
            </tr>
          </tfoot>
        </Table>
      </div>
    </>
  );
};

export default SummaryTabBase;
